'use client'

import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { useTheme } from 'next-themes'
import FinancialChart, { type ChartType, type OhlcvDataPoint, type OhlcvCrosshair } from '@/components/FinancialChart'
import { fetchIndexQuotes, fetchIndexOhlcvData, fetchIndexConstituents, type ConstituentStock } from '@/services/indexApi'
import { fetchQuote } from '@/services/marketApi'
import { indexInfo } from '@/data/indexData'
import InstrumentAbout from '@/components/InstrumentAbout'
import { useSignalR } from '@/hooks/useSignalR'
import type { PriceUpdate } from '@/services/signalrService'

interface Props {
  id: string
}

const periods = [
  { key: '1H', tr: '1H', en: '1W' },
  { key: '1A', tr: '1A', en: '1M' },
  { key: '3A', tr: '3A', en: '3M' },
  { key: '1Y', tr: '1Y', en: '1Y' },
  { key: '5Y', tr: '5Y', en: '5Y' },
  { key: 'all', tr: 'Tümü', en: 'All' },
]

/* --- Helpers --- */

type CurrencyType = 'usd' | 'eur' | 'jpy' | 'hkd' | 'cny' | 'krw' | 'inr'

const currencySymbols: Record<CurrencyType, string> = {
  usd: '$', eur: '\u20AC', jpy: '\u00A5', hkd: 'HK$', cny: '\u00A5', krw: '\u20A9', inr: '\u20B9',
}

function fmtVal(val: number, currency?: CurrencyType): string {
  if (currency) {
    const sym = currencySymbols[currency]
    if (currency === 'krw' || currency === 'jpy') return sym + Math.round(val).toLocaleString('en-US')
    return sym + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  const [int, dec] = val.toFixed(2).split('.')
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec
}

function fmtVol(vol: number, currency?: CurrencyType): string {
  const prefix = currency ? currencySymbols[currency] : ''
  if (currency) {
    if (vol >= 1_000_000_000_000) return prefix + (vol / 1_000_000_000_000).toFixed(2) + 'T'
    if (vol >= 1_000_000_000) return prefix + (vol / 1_000_000_000).toFixed(2) + 'B'
    if (vol >= 1_000_000) return prefix + (vol / 1_000_000).toFixed(2) + 'M'
    if (vol >= 1_000) return prefix + (vol / 1_000).toFixed(1) + 'K'
    return prefix + vol.toLocaleString('en-US')
  }
  if (vol >= 1_000_000_000) return (vol / 1_000_000_000).toFixed(2).replace('.', ',') + ' Mrd'
  if (vol >= 1_000_000) return (vol / 1_000_000).toFixed(2).replace('.', ',') + ' Mln'
  if (vol >= 1_000) return (vol / 1_000).toFixed(1).replace('.', ',') + ' B'
  return vol.toLocaleString('tr-TR')
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

/* --- Chart Type Icons --- */
function IconCandle({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="2" x2="8" y2="6" /><rect x="5.5" y="6" width="5" height="10" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="8" y1="16" x2="8" y2="22" />
      <line x1="17" y1="4" x2="17" y2="8" /><rect x="14.5" y="8" width="5" height="8" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="17" y1="16" x2="17" y2="20" />
    </svg>
  )
}

function IconLine({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 18 8 12 14 16 22 6" />
    </svg>
  )
}

function IconArea({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20 L8 12 L14 16 L22 6 L22 20 Z" fill={active ? 'rgba(52,152,219,0.2)' : 'rgba(128,128,128,0.1)'} />
      <polyline points="2 20 8 12 14 16 22 6" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" fill="none" />
    </svg>
  )
}

/* --- Page --- */

export default function IndexDetailClient({ id }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  interface QuoteInfo {
    price: number; change: number; changePercent: number
    dayHigh: number; dayLow: number; previousClose: number; volume: number
    week52High?: number; week52Low?: number
  }

  const [quote, setQuote] = useState<QuoteInfo | null>(null)
  const [ohlcvData, setOhlcvData] = useState<OhlcvDataPoint[]>([])
  const [period, setPeriod] = useState('1A')
  const [chartType, setChartType] = useState<ChartType>('candlestick')
  const [crosshair, setCrosshair] = useState<OhlcvCrosshair | null>(null)
  const [stocks, setStocks] = useState<ConstituentStock[]>([])
  const [stocksTotalCount, setStocksTotalCount] = useState(0)
  const [stocksPage, setStocksPage] = useState(1)
  const [chartLoading, setChartLoading] = useState(true)
  const [stocksLoading, setStocksLoading] = useState(true)
  const [chartUpdatedAt, setChartUpdatedAt] = useState<number>(0)
  const [notFound, setNotFound] = useState(false)
  const [sortCol, setSortCol] = useState<string>('changePercent')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [stockSearch, setStockSearch] = useState('')
  const [backendInfo, setBackendInfo] = useState<{ name: string; description: string; symbol: string } | null>(null)
  const stocksPageSize = 20

  const hardcoded = id ? indexInfo[id] : undefined
  const info = hardcoded || backendInfo
  const market = hardcoded?.market

  // Ağustos 2026 denetimi: açıklama tek dilliydi, İngilizce sayfada
  // "(Nasdaq 100 Endeksi)" gibi Türkçe metin sızıyordu. Gösterim artık dile göre.
  // Backend fallback'i yalnızca tek dil döndürdüğü için orada olduğu gibi kullanılır.
  const isEn = lang === 'en'
  const displayName = hardcoded
    ? (isEn ? (hardcoded.nameEn ?? hardcoded.name) : hardcoded.name)
    : (backendInfo?.name ?? '')
  const displayDescription = hardcoded
    ? (isEn ? hardcoded.descriptionEn : hardcoded.description)
    : (backendInfo?.description ?? '')

  // Backend fallback
  useEffect(() => {
    if (hardcoded || !id) return
    const symbol = id.toUpperCase()
    fetchQuote(symbol).then(q => {
      if (q) {
        setBackendInfo({ name: q.name, description: q.symbol, symbol: q.symbol })
        setQuote({
          price: q.close, change: q.diff, changePercent: q.changePercent,
          dayHigh: q.high, dayLow: q.low,
          previousClose: q.previousClose ?? q.close, volume: q.volume ?? 0,
        })
      } else {
        setNotFound(true)
      }
    }).catch(() => setNotFound(true))
  }, [id, hardcoded])

  // Index price
  const loadQuote = useCallback(async () => {
    if (!hardcoded || !id) return
    {
      // BIST endeksleri kaldırıldı — geriye yalnızca dünya endeksleri kaldı.
      const data = await fetchIndexQuotes([hardcoded.symbol])
      const q = data[hardcoded.symbol]
      if (q) {
        setQuote({
          price: q.price, change: q.change, changePercent: q.changePercent,
          dayHigh: q.high, dayLow: q.low,
          previousClose: q.previousClose, volume: 0,
          week52High: q.week52High, week52Low: q.week52Low,
        })
      }
    }
  }, [hardcoded, id])

  useEffect(() => {
    if (!hardcoded) return
    loadQuote()
    const iv = setInterval(loadQuote, 60_000)
    return () => clearInterval(iv)
  }, [loadQuote, hardcoded])

  // OHLCV chart data
  useEffect(() => {
    if (!info) return
    setChartLoading(true)
    setCrosshair(null)
    const symbol = info.symbol
    fetchIndexOhlcvData(symbol, period).then(result => {
      setOhlcvData(result?.data ?? [])
      setChartUpdatedAt(Date.now())
      setChartLoading(false)
    }).catch(() => { setOhlcvData([]); setChartLoading(false) })
  }, [info, period])

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(stockSearch)
      setStocksPage(1)
    }, 350)
    return () => clearTimeout(timer)
  }, [stockSearch])

  // Constituent stocks
  useEffect(() => {
    if (!info) { setStocksLoading(false); return }
    setStocksLoading(true)
    fetchIndexConstituents(info.symbol, stocksPage, stocksPageSize, debouncedSearch || undefined).then(res => {
      setStocks(res.items)
      setStocksTotalCount(res.totalCount)
      setStocksLoading(false)
    }).catch(() => setStocksLoading(false))
  }, [info, stocksPage, debouncedSearch])

  /* --- SignalR Live Price (Constituent Stocks) --- */
  const handleConstituentPriceUpdate = useCallback((update: PriceUpdate) => {
    setStocks(prev => {
      const idx = prev.findIndex(s => s.symbol === update.symbol)
      if (idx === -1) return prev
      if (prev[idx].price === update.price) return prev
      const updated = [...prev]
      updated[idx] = { ...updated[idx], price: update.price }
      return updated
    })
  }, [])

  useSignalR({
    onPriceUpdate: handleConstituentPriceUpdate,
  })

  // 404
  if (notFound) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4">
        <div className="text-center py-24">
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            {lang === 'en' ? 'Index not found' : 'Endeks bulunamadı'}
          </h1>
          <button
            onClick={() => router.push('/')}
            className="px-6 py-2.5 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
          >
            {lang === 'en' ? 'Back to Home' : 'Ana Sayfaya Dön'}
          </button>
        </div>
      </main>
    )
  }

  // Loading
  if (!info) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
        <div className="flex items-center gap-2 text-[13px] mb-4">
          <Skeleton w={60} h={14} />
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <Skeleton w={120} h={14} />
        </div>
        <Skeleton w={300} h={28} className="mb-3" />
        <div className="rounded-xl border p-0 mb-5" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="animate-pulse rounded-lg m-4" style={{ height: 480, backgroundColor: 'var(--border-color)', opacity: 0.3 }} />
        </div>
      </main>
    )
  }

  const isUp = quote ? quote.changePercent >= 0 : true
  const clr = isUp ? 'var(--chart-up)' : 'var(--chart-down)'
  const idxCur: CurrencyType | undefined = undefined

  // OHLC display data (crosshair or last data)
  const lastOhlcv = ohlcvData.length > 0 ? ohlcvData[ohlcvData.length - 1] : null
  const ohlcDisplay = crosshair || (lastOhlcv ? {
    time: lastOhlcv.time,
    open: lastOhlcv.open,
    high: lastOhlcv.high,
    low: lastOhlcv.low,
    close: lastOhlcv.close,
    volume: lastOhlcv.volume,
  } : null)

  // Sorted stocks
  const displayStocks = [...stocks]
    .sort((a, b) => {
      let cmp = 0
      switch (sortCol) {
        case 'symbol': cmp = a.symbol.localeCompare(b.symbol); break
        case 'price': cmp = a.price - b.price; break
        case 'changePercent': cmp = a.changePercent - b.changePercent; break
        case 'monthly': cmp = (a.monthly ?? -Infinity) - (b.monthly ?? -Infinity); break
        case 'yearly': cmp = (a.yearly ?? -Infinity) - (b.yearly ?? -Infinity); break
        case 'volume': cmp = a.volume - b.volume; break
        case 'weight': cmp = (a.weight ?? 0) - (b.weight ?? 0); break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
  const maxWeight = Math.max(...stocks.map(s => s.weight ?? 0), 1)
  const hasWeights = stocks.some(s => s.weight != null && s.weight > 0)
  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const marketLabel =
    market === 'us' ? (lang === 'en' ? 'US Markets' : 'ABD Borsası')
    : market === 'eu' ? (lang === 'en' ? 'European Markets' : 'Avrupa Borsası')
    : market === 'jp' ? (lang === 'en' ? 'Japan' : 'Japonya Borsası')
    : market === 'hk' ? (lang === 'en' ? 'Hong Kong/China' : 'Hong Kong/Çin')
    : market === 'kr' ? (lang === 'en' ? 'South Korea' : 'Güney Kore')
    : market === 'in' ? (lang === 'en' ? 'India' : 'Hindistan Borsası')
    : market === 'apac' ? (lang === 'en' ? 'Asia-Pacific' : 'Asya-Pasifik')
    : market === 'mea' ? (lang === 'en' ? 'Middle East & Africa' : 'Ortadoğu & Afrika')
    : market === 'americas' ? (lang === 'en' ? 'Americas' : 'Amerika Kıtası')
    : market === 'commodity' ? (lang === 'en' ? 'Commodities' : 'Emtia')
    : (lang === 'en' ? 'Market Data' : 'Piyasa Verileri')

  return (
    <main className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[12px] mb-4 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors" style={{ color: 'var(--accent-color)' }}>
          {lang === 'en' ? 'Home' : 'Anasayfa'}
        </Link>
        <span className="select-none">/</span>
        <span>{marketLabel}</span>
        <span className="select-none">/</span>
        <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{displayName}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight m-0 flex items-baseline gap-2" style={{ color: 'var(--text-main)' }}>
            {displayName}
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              ({displayDescription})
            </span>
          </h1>
        </div>
        {quote ? (
          <div className="flex items-baseline gap-3 shrink-0">
            <span className="text-[28px] font-bold tabular-nums" style={{ color: clr }}>
              {fmtVal(quote.price, idxCur)}
            </span>
            <span className="text-[15px] font-semibold tabular-nums" style={{ color: clr }}>
              {isUp ? '+' : ''}{fmtVal(quote.change, idxCur)}
              {' '}({isUp ? '+' : ''}%{Math.abs(quote.changePercent).toFixed(2).replace('.', ',')})
              <svg
                className="inline-block ml-1"
                width="10" height="10" viewBox="0 0 10 10"
                fill={clr}
              >
                {isUp
                  ? <polygon points="5,1 9,7 1,7" />
                  : <polygon points="5,9 9,3 1,3" />
                }
              </svg>
            </span>
          </div>
        ) : (
          <div className="flex items-baseline gap-3">
            <Skeleton w={140} h={28} />
            <Skeleton w={100} h={18} />
          </div>
        )}
      </div>

      {/* Hisselere atla CTA — chart'ın altında 3000+ hisse var, kullanıcı fark etsin */}
      {stocksTotalCount > 0 && (
        <button
          type="button"
          onClick={() => {
            const el = document.getElementById('index-constituents')
            if (!el) return
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setTimeout(() => {
              const input = document.getElementById('constituent-search-input') as HTMLInputElement | null
              input?.focus({ preventScroll: true })
            }, 650)
          }}
          className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 mb-4 px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-all hover:brightness-95"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--accent-color) 12%, transparent)',
            color: 'var(--accent-color)',
            border: '1px solid color-mix(in srgb, var(--accent-color) 35%, transparent)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {lang === 'en'
            ? `Explore ${stocksTotalCount.toLocaleString('en-US')} stocks in this index →`
            : `Bu endeksteki ${stocksTotalCount.toLocaleString('tr-TR')} hisseyi keşfet →`}
        </button>
      )}

      {/* Chart Card */}
      <div
        className="rounded-xl border overflow-hidden mb-5"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        {/* OHLC Data Row */}
        {ohlcDisplay && (
          <div
            className="flex items-center gap-4 sm:gap-6 px-4 sm:px-5 pt-3 pb-2 flex-wrap"
            style={{ borderBottom: '1px solid var(--border-color)' }}
          >
            {[
              { label: 'O', value: ohlcDisplay.open },
              { label: 'H', value: ohlcDisplay.high, color: 'var(--chart-up)' },
              { label: 'L', value: ohlcDisplay.low, color: 'var(--chart-down)' },
              { label: 'C', value: ohlcDisplay.close },
            ].map(item => (
              <div key={item.label} className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: item.color || 'var(--text-main)' }}>
                  {fmtVal(item.value, idxCur)}
                </span>
              </div>
            ))}
            {ohlcDisplay.volume != null && ohlcDisplay.volume > 0 && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Vol</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>
                  {fmtVol(ohlcDisplay.volume, idxCur)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div
          className="flex items-center justify-between px-4 sm:px-5 py-2"
          style={{ borderBottom: '1px solid var(--border-color)' }}
        >
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {periods.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className="px-3 py-1 rounded-md text-[12px] font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: period === p.key ? 'var(--accent-color)' : 'transparent',
                  color: period === p.key ? '#fff' : 'var(--text-muted)',
                }}
              >
                {lang === 'en' ? p.en : p.tr}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0.5 ml-3 shrink-0">
            <div className="h-4 w-px mx-2" style={{ backgroundColor: 'var(--border-color)' }} />
            {([
              { type: 'candlestick' as ChartType, Icon: IconCandle },
              { type: 'line' as ChartType, Icon: IconLine },
              { type: 'area' as ChartType, Icon: IconArea },
            ]).map(({ type, Icon }) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className="p-1.5 rounded transition-colors"
                style={{
                  backgroundColor: chartType === type ? (isDark ? 'rgba(52,152,219,0.15)' : 'rgba(52,152,219,0.1)') : 'transparent',
                }}
                title={type}
              >
                <Icon active={chartType === type} />
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="px-0">
          {chartLoading ? (
            <div className="animate-pulse" style={{ height: 480, backgroundColor: 'var(--border-color)', opacity: 0.15 }} />
          ) : ohlcvData.length === 0 ? (
            <div className="flex items-center justify-center" style={{ height: 480 }}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {lang === 'en' ? 'No chart data available' : 'Bu endeks için grafik verisi bulunamadı'}
              </span>
            </div>
          ) : (
            <FinancialChart
              data={ohlcvData}
              isDark={isDark}
              height={480}
              chartType={chartType}
              showVolume={true}
              onCrosshairMove={setCrosshair}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-2 text-[11px]" style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--border-color)' }}>
          {lang === 'en' ? 'Last updated' : 'Son güncelleme'}: {chartUpdatedAt > 0
            ? (() => {
                const tz = 'Europe/Istanbul'
                const d = new Date(chartUpdatedAt)
                return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { timeZone: tz }) + ' ' + d.toLocaleTimeString(lang === 'en' ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: tz })
              })()
            : '\u2014'}
        </div>
      </div>

      {/* Info Bar */}
      {quote && (
        <div
          className="rounded-xl border overflow-hidden mb-5 px-5 py-4"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5`}>
            {[
              { label: lang === 'en' ? 'Close' : 'Kapanış', value: fmtVal(quote.price, idxCur), color: 'var(--text-main)' },
              { label: lang === 'en' ? 'Day Low' : 'En Düşük', value: fmtVal(quote.dayLow, idxCur), color: 'var(--chart-down)' },
              { label: lang === 'en' ? 'Day High' : 'En Yüksek', value: fmtVal(quote.dayHigh, idxCur), color: 'var(--chart-up)' },
              { label: lang === 'en' ? 'Prev. Close' : 'Önceki Kapanış', value: fmtVal(quote.previousClose, idxCur), color: 'var(--text-main)' },
              ...(quote.volume > 0 ? [{ label: lang === 'en' ? 'Volume' : 'Hacim', value: fmtVol(quote.volume, idxCur), color: 'var(--text-main)' }] : []),
              ...(quote.week52High ? [
                { label: lang === 'en' ? '52W High' : '52H Yüksek', value: fmtVal(quote.week52High), color: 'var(--chart-up)' },
                { label: lang === 'en' ? '52W Low' : '52H Düşük', value: fmtVal(quote.week52Low!), color: 'var(--chart-down)' },
              ] : []),
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
                <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Constituent Stocks */}
      {(stocksLoading || stocks.length > 0) && (
        <div
          id="index-constituents"
          className="rounded-xl border overflow-hidden mb-5 scroll-mt-20"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          {/* Header + Search */}
          <div className="p-4 sm:p-5 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-bold m-0" style={{ color: 'var(--text-main)' }}>
                {lang === 'en' ? 'Index Constituents' : 'Endeks Hisse Senetleri'}
              </h2>
              {stocksTotalCount > 0 && (
                <span
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--bg-body)', color: 'var(--text-muted)' }}
                >
                  {stocksTotalCount}
                </span>
              )}
            </div>
            {stocks.length > 0 && (
              <div className="relative w-full sm:w-auto">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  id="constituent-search-input"
                  type="text"
                  value={stockSearch}
                  onChange={e => setStockSearch(e.target.value)}
                  placeholder={lang === 'en' ? 'Search stocks...' : 'Hisse ara...'}
                  className="constituent-search pl-9 pr-3 py-2 rounded-lg text-[13px] outline-none w-full sm:w-64 transition-shadow"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-main)',
                    border: '1.5px solid var(--accent-color)',
                    boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent-color) 14%, transparent)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse" style={{ minWidth: 720 }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-body)' }}>
                  {[
                    { key: 'symbol', label: lang === 'en' ? 'Stock' : 'Hisse', align: 'left' as const, px: 'pl-5 pr-3' },
                    ...(hasWeights ? [{ key: 'weight', label: lang === 'en' ? 'Weight' : 'Ağırlık', align: 'left' as const, px: 'px-3' }] : []),
                    { key: 'price', label: lang === 'en' ? 'Price' : 'Fiyat', align: 'right' as const, px: 'px-3' },
                    { key: 'changePercent', label: lang === 'en' ? 'Change' : 'Değişim', align: 'right' as const, px: 'px-3' },
                    { key: 'monthly', label: lang === 'en' ? 'Monthly' : 'Aylık', align: 'right' as const, px: 'px-3' },
                    { key: 'yearly', label: lang === 'en' ? 'Yearly' : 'Yıllık', align: 'right' as const, px: 'px-3' },
                    { key: 'volume', label: lang === 'en' ? 'Volume' : 'Hacim', align: 'right' as const, px: 'pr-5 pl-3' },
                  ].map(h => (
                    <th scope="col"
                      key={h.key}
                      onClick={() => toggleSort(h.key)}
                      className={`py-2.5 text-[10px] font-bold uppercase tracking-widest ${h.px} cursor-pointer select-none transition-colors hover:opacity-80`}
                      style={{
                        color: sortCol === h.key ? 'var(--accent-color)' : 'var(--text-muted)',
                        borderBottom: '2px solid var(--border-color)',
                        textAlign: h.align,
                      }}
                    >
                      {h.label}
                      <span className="ml-1 text-[9px]">
                        {sortCol === h.key ? (sortDir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stocksLoading
                  ? Array.from({ length: stocksPageSize }).map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td className="pl-5 pr-3 py-3"><Skeleton w={100} /></td>
                        {hasWeights && <td className="px-3 py-3"><Skeleton w={60} /></td>}
                        <td className="px-3 py-3 text-right"><Skeleton w={60} className="ml-auto" /></td>
                        <td className="px-3 py-3 text-right"><Skeleton w={56} className="ml-auto" /></td>
                        <td className="px-3 py-3 text-right"><Skeleton w={50} className="ml-auto" /></td>
                        <td className="px-3 py-3 text-right"><Skeleton w={50} className="ml-auto" /></td>
                        <td className="pr-5 pl-3 py-3 text-right"><Skeleton w={70} className="ml-auto" /></td>
                      </tr>
                    ))
                  : displayStocks.map((s, i) => {
                      const up = s.changePercent >= 0
                      const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
                      return (
                        <tr
                          key={s.symbol}
                          className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
                          style={{ borderBottom: i < displayStocks.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                          onClick={() => router.push({ pathname: '/stock/[symbol]', params: { symbol: s.symbol } })}
                        >
                          <td className="pl-5 pr-3 py-2.5">
                            <div className="flex items-center gap-2.5">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
                                style={{ backgroundColor: up ? 'rgba(76,175,80,0.1)' : 'rgba(229,57,53,0.1)', color: c }}
                              >
                                {s.symbol.slice(0, 2)}
                              </div>
                              <div>
                                <div className="text-[13px] font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{s.symbol}</div>
                                <div className="text-[10px] mt-0.5 leading-tight truncate max-w-[160px]" style={{ color: 'var(--text-muted)' }}>{s.name}</div>
                              </div>
                            </div>
                          </td>
                          {hasWeights && (
                            <td className="px-3 py-2.5">
                              {s.weight != null ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                                    <div className="h-full rounded-full transition-all" style={{ width: `${(s.weight / maxWeight) * 100}%`, backgroundColor: 'var(--accent-color)', opacity: 0.7 }} />
                                  </div>
                                  <span className="text-[11px] tabular-nums font-medium" style={{ color: 'var(--text-muted)' }}>
                                    %{s.weight.toFixed(1).replace('.', ',')}
                                  </span>
                                </div>
                              ) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                            </td>
                          )}
                          <td className="px-3 py-2.5 text-right">
                            <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtVal(s.price)}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums"
                              style={{ backgroundColor: up ? 'rgba(76,175,80,0.12)' : 'rgba(229,57,53,0.12)', color: c }}
                            >
                              {up ? '+' : ''}%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {s.monthly != null ? (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums"
                                style={{ backgroundColor: s.monthly >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(229,57,53,0.08)', color: s.monthly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}
                              >
                                {s.monthly >= 0 ? '+' : ''}%{Math.abs(s.monthly).toFixed(2).replace('.', ',')}
                              </span>
                            ) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {s.yearly != null ? (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums"
                                style={{ backgroundColor: s.yearly >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(229,57,53,0.08)', color: s.yearly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}
                              >
                                {s.yearly >= 0 ? '+' : ''}%{Math.abs(s.yearly).toFixed(2).replace('.', ',')}
                              </span>
                            ) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                          </td>
                          <td className="pr-5 pl-3 py-2.5 text-right">
                            <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                              {fmtVol(s.volume)}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {stocksLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <Skeleton w={120} h={16} className="mb-2" />
                    <Skeleton w={200} h={12} />
                  </div>
                ))
              : displayStocks.map((s, i) => {
                  const up = s.changePercent >= 0
                  const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
                  return (
                    <div
                      key={s.symbol}
                      className="px-4 py-3 cursor-pointer transition-colors active:opacity-80"
                      style={{ borderBottom: i < displayStocks.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                      onClick={() => router.push({ pathname: '/stock/[symbol]', params: { symbol: s.symbol } })}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0"
                            style={{ backgroundColor: up ? 'rgba(76,175,80,0.1)' : 'rgba(229,57,53,0.1)', color: c }}
                          >
                            {s.symbol.slice(0, 2)}
                          </div>
                          <div>
                            <span className="text-[13px] font-bold" style={{ color: 'var(--text-main)' }}>{s.symbol}</span>
                            <div className="text-[10px] leading-tight truncate max-w-[140px]" style={{ color: 'var(--text-muted)' }}>{s.name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[14px] font-bold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtVal(s.price)}</div>
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums"
                            style={{ backgroundColor: up ? 'rgba(76,175,80,0.12)' : 'rgba(229,57,53,0.12)', color: c }}
                          >
                            {up ? '+' : ''}%{Math.abs(s.changePercent).toFixed(2).replace('.', ',')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {s.monthly != null && (
                          <span className="text-[10px] tabular-nums" style={{ color: s.monthly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                            {lang === 'en' ? 'M' : 'Ay'}: {s.monthly >= 0 ? '+' : ''}%{Math.abs(s.monthly).toFixed(1).replace('.', ',')}
                          </span>
                        )}
                        {s.yearly != null && (
                          <span className="text-[10px] tabular-nums" style={{ color: s.yearly >= 0 ? 'var(--chart-up)' : 'var(--chart-down)' }}>
                            {lang === 'en' ? 'Y' : 'Yıl'}: {s.yearly >= 0 ? '+' : ''}%{Math.abs(s.yearly).toFixed(1).replace('.', ',')}
                          </span>
                        )}
                        {s.volume > 0 && (
                          <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                            Vol: {fmtVol(s.volume)}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
          </div>

          {/* No search results */}
          {!stocksLoading && debouncedSearch && stocks.length === 0 && (
            <div className="py-8 text-center">
              <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                {lang === 'en' ? 'No stocks found' : 'Hisse bulunamadı'}
              </span>
            </div>
          )}

          {/* Pagination */}
          {stocksTotalCount > stocksPageSize && (() => {
            const totalPages = Math.ceil(stocksTotalCount / stocksPageSize)
            const pages: (number | 'dots')[] = []
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) pages.push(i)
            } else {
              pages.push(1)
              if (stocksPage > 3) pages.push('dots')
              const start = Math.max(2, stocksPage - 1)
              const end = Math.min(totalPages - 1, stocksPage + 1)
              for (let i = start; i <= end; i++) pages.push(i)
              if (stocksPage < totalPages - 2) pages.push('dots')
              pages.push(totalPages)
            }
            return (
              <div className="flex items-center justify-between px-4 sm:px-5 py-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                  {((stocksPage - 1) * stocksPageSize) + 1}&ndash;{Math.min(stocksPage * stocksPageSize, stocksTotalCount)} / {stocksTotalCount}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setStocksPage(p => Math.max(1, p - 1))}
                    disabled={stocksPage <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:opacity-30"
                    style={{ color: 'var(--text-main)' }}
                  >
                    &lsaquo;
                  </button>
                  {pages.map((p, idx) =>
                    p === 'dots' ? (
                      <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px]" style={{ color: 'var(--text-muted)' }}>&hellip;</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setStocksPage(p)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-semibold transition-colors"
                        style={{
                          backgroundColor: stocksPage === p ? 'var(--accent-color)' : 'transparent',
                          color: stocksPage === p ? '#fff' : 'var(--text-muted)',
                        }}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setStocksPage(p => p + 1)}
                    disabled={stocksPage >= totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:opacity-30"
                    style={{ color: 'var(--text-main)' }}
                  >
                    &rsaquo;
                  </button>
                </div>
              </div>
            )
          })()}
        </div>
      )}
      <InstrumentAbout symbol={id.toUpperCase()} kind="index" />
    </main>
  )
}
